(() => {
  window.CKB_FIELD_HANDBOOK = { version: '1.1.0', state: 'loading' };
  const INTERNAL_SENTENCES = [
    /This chapter expands the original handbook entry without changing its stable identity\.?/gi,
    /This article expands the original handbook entry without changing its stable identity\.?/gi,
    /Open the Product DNA page for [^.]+\.?/gi,
    /Product pages sell; the handbook teaches; the user guide controls operation\.?/gi,
    /The handbook teaches why; the product page sells; the user guide controls operation\.?/gi,
    /The handbook teaches the engineering and must not invent a recipe from connector fit\.?/gi
  ];

  const SHARED_HEADINGS = new Set([
    'define the operating requirement',
    'system engineering',
    'documented field procedure',
    'verified compatibility boundary',
    'worked field interpretation',
    'performance interpretation',
    'product-specific questions to answer',
    'common errors',
    'safety and stop-work conditions',
    'source and revision note',
    'engineering boundaries',
    'verified compatibility rule',
    'configuration record',
    'when this approach is not enough',
    'product and buying path',
    'sources and review',
    'what changes the result',
    'compatibility that matters',
    'what to record',
    'stop and check the current guide',
    'product and buying options'
  ]);

  const HEADING_RENAMES = {
    'purpose': 'Start Here',
    'explanation': 'The Practical Answer',
    'define the operating requirement': 'Before You Choose',
    'system engineering': 'What Changes the Result',
    'documented field procedure': 'A Repeatable Field Workflow',
    'verified compatibility boundary': 'Compatibility That Matters',
    'worked field interpretation': 'What This Looks Like in Practice',
    'performance interpretation': 'How to Read the Result',
    'product-specific questions to answer': 'Questions to Answer Before You Buy or Deploy',
    'common errors': 'Avoid These Mistakes',
    'safety and stop-work conditions': 'Stop If Any of These Are True',
    'where to continue': 'What to Do Next',
    'source and revision note': 'Sources and Review',
    'purpose and operator outcome': 'Start Here',
    'start with the mission': 'Before You Choose',
    'decision workflow': 'How to Decide',
    'worked field example': 'In the Field',
    'configuration record': 'What to Record',
    'common mistakes': 'Avoid These Mistakes',
    'when this approach is not enough': 'Stop and Check the Current Guide',
    'related handbook pages': 'Keep Learning',
    'product and buying path': 'Product and Buying Options'
  };

  const INLINE_LABELS = [
    'At a Glance', 'Type', 'Best For', 'Architecture', 'Difficulty', 'MPAS READY', 'Quick Answer',
    'Choose MPAS 2.0 When', 'Choose MPAS Lite When', 'Overview', 'Why It Matters', 'Why This Matters',
    'Why HF Is Different', 'Benefits', 'Advantages',
    'Limitations', 'Common Causes', 'Common Symptoms', 'Possible Effects', 'Possible Solutions',
    'Applications', 'Typical Applications', 'Best Practices', 'Good Operating Practice',
    'Installation Tips', 'Important Considerations', 'Important Limitations', 'How It Works',
    'How to Take Advantage of Gray Line', 'Applied to Chameleon Products', 'Related Articles',
    'Related Products', 'Portable Operation', 'Receiver Improvements', 'The Amateur HF Bands',
    'Near Field', 'Far Field', 'Characteristics Include', 'Before Each Deployment',
    'Every Six Months', 'Annual Inspection', 'Storage', 'Maintenance Tip',
    'What Is a Balun?', 'What Is a Unun?', 'Common Ratios', 'Common Power Sources',
    'Selection Factors', 'Power Management Tips'
  ];

  const FACT_LABELS = new Set(['best for', 'difficulty', 'mpas ready']);
  const INTERNAL_FACT_LABELS = new Set(['type', 'architecture']);

  const normalized = value => (value || '').toString().replace(/\s+/g, ' ').trim();
  const headingKey = value => normalized(value).toLowerCase();
  const escapeRegExp = value => value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

  const cleanText = value => INTERNAL_SENTENCES.reduce((text, pattern) => text.replace(pattern, ' '), value)
    .replace(/Use the Product DNA page for/gi, 'Use the current product page for')
    .replace(/Product DNA/gi, 'current product page')
    .replace(/current the applicable/gi, 'current applicable')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();

  const trimClippedParagraph = paragraph => {
    const text = normalized(paragraph.textContent);
    if (text.length < 1185 || /[.!?…:)\]”’]$/.test(text)) return;
    const last = Math.max(text.lastIndexOf('. '), text.lastIndexOf('? '), text.lastIndexOf('! '));
    if (last > Math.max(220, text.length * 0.72)) paragraph.textContent = text.slice(0, last + 1);
  };

  const splitLongParagraph = paragraph => {
    if (paragraph.children.length || normalized(paragraph.textContent).length < 520) return;
    let text = normalized(paragraph.textContent).replace(/^Overview\s+/i, '');
    const pattern = new RegExp(`\\s+(${INLINE_LABELS.map(escapeRegExp).join('|')})\\s+`, 'g');
    const pieces = [];
    let cursor = 0;
    for (const match of text.matchAll(pattern)) {
      if (match.index > cursor) pieces.push({ type: 'text', value: text.slice(cursor, match.index).trim() });
      pieces.push({ type: 'label', value: match[1] });
      cursor = match.index + match[0].length;
    }
    if (cursor < text.length) pieces.push({ type: 'text', value: text.slice(cursor).trim() });
    if (!pieces.some(piece => piece.type === 'label')) {
      const sentences = text.match(/[^.!?]+(?:[.!?]+[”’]?|$)/g)?.map(normalized).filter(Boolean) || [];
      if (sentences.length < 4) return;
      pieces.push(...sentences.reduce((groups, sentence, index) => {
        const group = Math.floor(index / 2);
        groups[group] = `${groups[group] || ''} ${sentence}`.trim();
        return groups;
      }, []).map(value => ({ type: 'text', value })));
    }
    const fragment = document.createDocumentFragment();
    pieces.filter(piece => piece.value).forEach(piece => {
      const node = document.createElement(piece.type === 'label' ? 'h3' : 'p');
      if (piece.type === 'label') node.className = 'ckb-field-handbook__subhead';
      node.textContent = piece.value;
      fragment.appendChild(node);
    });
    if (fragment.childNodes.length > 1) paragraph.replaceWith(fragment);
  };

  const wrapSharedNotes = root => {
    const headings = Array.from(root.querySelectorAll('h2'));
    const shared = [];
    headings.forEach(heading => {
      if (!SHARED_HEADINGS.has(headingKey(heading.textContent))) return;
      const nodes = [heading];
      let next = heading.nextElementSibling;
      while (next && next.tagName !== 'H2') {
        nodes.push(next);
        next = next.nextElementSibling;
      }
      shared.push(...nodes);
    });
    if (!shared.length) return;
    const details = document.createElement('details');
    details.className = 'ckb-field-handbook__technical-notes';
    const summary = document.createElement('summary');
    summary.innerHTML = '<span>Technical notes, safety, and sources</span><small>Open when you need the deeper engineering boundary.</small>';
    details.appendChild(summary);
    const body = document.createElement('div');
    body.className = 'ckb-field-handbook__technical-notes-body';
    shared.forEach(node => body.appendChild(node));
    details.appendChild(body);
    root.appendChild(details);
  };

  const compactFacts = root => {
    root.querySelectorAll('p').forEach(paragraph => {
      if (headingKey(paragraph.textContent) === 'at a glance') paragraph.remove();
    });
    const facts = [];
    root.querySelectorAll('h3.ckb-field-handbook__subhead').forEach(heading => {
      const value = heading.nextElementSibling;
      if (!value || value.tagName !== 'P') return;
      if (INTERNAL_FACT_LABELS.has(headingKey(heading.textContent))) {
        heading.remove();
        value.remove();
        return;
      }
      if (!FACT_LABELS.has(headingKey(heading.textContent))) return;
      facts.push({ heading, value });
    });
    if (facts.length < 2) return;
    const list = document.createElement('dl');
    list.className = 'ckb-field-handbook__facts';
    facts.forEach(({ heading, value }) => {
      const item = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = heading.textContent;
      description.textContent = value.textContent;
      item.append(term, description);
      list.appendChild(item);
      heading.remove();
      value.remove();
    });
    const start = Array.from(root.querySelectorAll('h2')).find(heading => headingKey(heading.textContent) === 'start here');
    if (start) start.insertAdjacentElement('afterend', list); else root.prepend(list);
  };

  const addContents = root => {
    const headings = Array.from(root.querySelectorAll(':scope > h2')).filter(heading => !heading.closest('details'));
    if (headings.length < 3) return;
    const nav = document.createElement('nav');
    nav.className = 'ckb-field-handbook__contents';
    nav.setAttribute('aria-label', 'On this page');
    nav.innerHTML = '<strong>On this page</strong>';
    const list = document.createElement('div');
    headings.slice(0, 8).forEach((heading, index) => {
      heading.id ||= `ckb-field-section-${index + 1}`;
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      list.appendChild(link);
    });
    nav.appendChild(list);
    const lead = root.querySelector('.ckb-callout, p');
    if (lead) lead.insertAdjacentElement('afterend', nav); else root.prepend(nav);
  };

  const prepareElement = (root, item = {}) => {
    if (!root || root.dataset.ckbFieldHandbookReady === 'true') return root;
    root.dataset.ckbFieldHandbookReady = 'true';
    root.classList.add('ckb-field-handbook');
    root.querySelectorAll('p, li').forEach(node => {
      const cleaned = cleanText(node.textContent);
      if (!cleaned) node.remove(); else if (cleaned !== normalized(node.textContent)) node.textContent = cleaned;
    });
    root.querySelectorAll('p').forEach(trimClippedParagraph);
    root.querySelectorAll('p').forEach(splitLongParagraph);
    root.querySelectorAll('h2').forEach(heading => {
      const replacement = HEADING_RENAMES[headingKey(heading.textContent)];
      if (replacement) heading.textContent = replacement;
    });
    compactFacts(root);
    root.querySelectorAll('h2 + h2, h3 + h3').forEach(heading => heading.remove());
    wrapSharedNotes(root);
    addContents(root);
    root.dataset.ckbArticleHandle = item.handle || root.dataset.ckbArticleHandle || '';
    return root;
  };

  const prepareHtml = (item, html) => {
    const template = document.createElement('template');
    template.innerHTML = `<div>${html || ''}</div>`;
    const root = template.content.firstElementChild;
    prepareElement(root, item);
    return root.innerHTML;
  };

  window.CKB_FIELD_HANDBOOK = { prepareHtml, prepareElement, version: '1.1.0', state: 'ready' };
})();
